import { getData as getCountryData } from "country-list"

import { getStatusBadge } from "components/tournaments/constants"

import { PromotionCategory } from "services/promotions/type"

import { Tournament } from "types/tournament.types"

import { formatCryptoCurrency, formatFiatCurrency, isFiatCoin, UNAVAILABLE_DASH } from "util/formatter"

import { isMicroserviceTenant } from "config/featureFlags"

import { TableDataType } from "./eums"
import { TableColumnsInterface } from "./interfaces"
import { allRolesFormatted } from "./roles"

export const tablePageSize = [5, 10, 20, 50, 100, 200]
export const countryInfoList = getCountryData()
  .map((countryInfo) => ({ value: countryInfo.code, name: countryInfo.name }))
  .sort((a, b) => a.name.localeCompare(b.name))

export const tableUsersColumns: TableColumnsInterface[] = [
  {
    value: "created_at",
    title: "Created At",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "tenant_id",
    title: "Tenant",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "personal_profile.first_name",
    title: "First Name",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "personal_profile.last_name",
    title: "Last Name",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "username",
    title: "Username",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "activated",
    title: "Activated",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "email_address",
    title: "Email Address",
    format: TableDataType.SHORTENED_STRING,
    sorting: true
  },
  {
    value: "email_verified",
    title: "Email Verified",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "total_deposit_amount",
    title: "Total Deposit Amount",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    // Fail-closed (SOFT-554): in microservice mode a `null` from a downed financial-service arrives
    // with `financials_unavailable` — render a dash, never a misleading `$0`.
    customRender: (value, row) => (row?.financials_unavailable ? UNAVAILABLE_DASH : formatFiatCurrency(value ?? undefined, "$0.00"))
  },
  {
    value: "balance",
    title: "Total Balance",
    format: TableDataType.STRING,
    sorting: true,
    // SOFT-554: balance authority (FS + bonus-service) down → `null` + `balance_unavailable`.
    // Render a dash rather than a blank/`$0` cell (matches the Admins grid and transactions).
    customRender: (value, row) => (row?.balance_unavailable ? UNAVAILABLE_DASH : value)
  },
  {
    value: "usd_balance",
    title: "Real Balance",
    format: TableDataType.STRING,
    sorting: true,
    customRender: (value, row) => (row?.balance_unavailable ? UNAVAILABLE_DASH : value)
  },
  {
    value: "busd_balance",
    title: "Bonus Balance",
    format: TableDataType.STRING,
    sorting: true,
    customRender: (value, row) => (row?.balance_unavailable ? UNAVAILABLE_DASH : value)
  },
  {
    value: "vipLabel",
    title: "VIP",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "personal_profile.country",
    title: "Country",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "phone_number",
    title: "Phone Number",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "site_lang",
    title: "Language",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "personal_profile.timezone",
    title: "Timezone",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "wager",
    title: "Wager",
    format: TableDataType.STRING,
    sorting: false,
    // Fail-closed (SOFT-554): the backend sends a preformatted string, or `null` +
    // `financials_unavailable` when the microservice authority is down — show a dash, not blank/`$0`.
    customRender: (value, row) => (row?.financials_unavailable ? UNAVAILABLE_DASH : value)
  },
  {
    value: "id",
    title: "Id",
    format: TableDataType.SHORTENED_STRING,
    // link: (id: string) => `/user/${id}`,
    sorting: true
  },
  {
    value: "gameSessions",
    title: "Sessions",
    format: TableDataType.JSON,
    sorting: false
  },
  {
    value: "last_login_at",
    title: "Last Login",
    format: TableDataType.DATE,
    sorting: true,
    mntSorting: false
  },
  {
    value: "last_deposit_at",
    title: "Last Deposit",
    format: TableDataType.DATE,
    sorting: false
  },
  {
    value: "last_bet_at",
    title: "Last Bet",
    format: TableDataType.DATE,
    sorting: false
  }
]

export const tableAdminsColumns: TableColumnsInterface[] = [
  {
    value: "created_at",
    title: "Created At",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "tenant_id",
    title: "Tenant",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "personal_profile.first_name",
    title: "First Name",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "personal_profile.last_name",
    title: "Last Name",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "username",
    title: "Username",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "role",
    title: "Role",
    format: TableDataType.DROPDOWN,
    dropdownOptions: allRolesFormatted,
    sorting: true
  },
  {
    value: "email_address",
    title: "Email Address",
    format: TableDataType.SHORTENED_STRING,
    sorting: true
  },
  {
    value: "total_deposit_amount",
    title: "Total Deposit Amount",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    // SOFT-557: admin rows now assemble FS-authoritative financials (fail-closed). Render a dash on
    // `financials_unavailable`, never a misleading `$0`.
    customRender: (value, row) => (row?.financials_unavailable ? UNAVAILABLE_DASH : formatFiatCurrency(value ?? undefined, "$0.00"))
  },
  {
    value: "balance",
    title: "Total Balance",
    format: TableDataType.STRING,
    sorting: true,
    // SOFT-557: balance authority (FS + bonus-service) down → `null` + `balance_unavailable`.
    customRender: (value, row) => (row?.balance_unavailable ? UNAVAILABLE_DASH : value)
  },
  {
    value: "usd_balance",
    title: "Real Balance",
    format: TableDataType.STRING,
    sorting: true,
    // SOFT-557: new column — FS real balance (previously absent on the Admins grid).
    customRender: (value, row) => (row?.balance_unavailable ? UNAVAILABLE_DASH : value)
  },
  {
    value: "busd_balance",
    title: "Bonus Balance",
    format: TableDataType.STRING,
    sorting: true,
    // SOFT-557: new column — bonus-service balance (previously absent on the Admins grid).
    customRender: (value, row) => (row?.balance_unavailable ? UNAVAILABLE_DASH : value)
  },
  {
    value: "personal_profile.country",
    title: "Country",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "phone_number",
    title: "Phone Number",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "site_lang",
    title: "Language",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "personal_profile.timezone",
    title: "Timezone",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "wager",
    title: "Wager",
    format: TableDataType.STRING,
    // NUX-owned and not rankable — the backend cannot order by it, so do not offer the sort.
    sorting: false,
    // SOFT-557: NUX-authoritative wager (string), or `null` + `financials_unavailable` on outage.
    customRender: (value, row) => (row?.financials_unavailable ? UNAVAILABLE_DASH : value)
  },
  {
    value: "id",
    title: "Id",
    format: TableDataType.SHORTENED_STRING,
    // link: (id: string) => `/user/${id}`,
    sorting: true
  },
  {
    value: "gameSessions",
    title: "Sessions",
    format: TableDataType.JSON,
    sorting: false
  }
]

/**
 * Money columns whose UNIT depends on which system produced the row — so each needs a per-tab
 * variant. The two bet tabs share every other column, but never these:
 *
 *  - NUX casino bets (microservice-owned) → NATIVE minor units of the row's `coin`. The NUX
 *    service never converts to USD (ADR-0013), and `mapNuxBetSummary` passes the amount through
 *    natively. Rendered against `row.coin`, so a PLN bet shows "PLN 12.34", never "$12.34".
 *  - BetBy bets (still monolith/MNT) → USD cents.
 *
 * The same number means a different currency per source, so sharing one column mislabels one of
 * them. `TableDataType.FIAT_CURRENCY` can't express this: it calls formatFiatCurrency with the
 * default currency ("USD") and has no access to the row. customRender does — it receives
 * (value, row) — which is how the coin reaches the formatter with no Table changes.
 */
const nativeMoneyColumn = (value: string, title: string, sorting = true): TableColumnsInterface => ({
  value,
  title,
  format: TableDataType.STRING,
  sorting,
  customRender: (cellValue, row) => formatFiatCurrency(cellValue, "-", row?.coin)
})

/**
 * A transaction's own `amount`, rendered in the units of its own `coin`.
 *
 * The (amount, coin) pair is polymorphic by design — see core-api `mapFsTransactionSummary`: a
 * crypto withdrawal carries the PAYOUT leg (crypto coin + crypto units), everything else carries
 * the wallet leg (minor units of a fiat coin). One `TableDataType` cannot render both, and
 * `CRYPTO_CURRENCY` on a fiat row divides by 1e8 — a $10.00 withdrawal reads as "0.00001".
 *
 * Only the legacy USD-only rows never set `amount`; they fall back to `usd_amount`.
 *
 * MICROSERVICE TENANTS ONLY. On monolith tenants `amount` is the pre-FS crypto figure, which is
 * exactly what `CRYPTO_CURRENCY` was written for — those keep their existing column.
 */
export const transactionAmountNativeColumn = (title = "Amount", sorting = true): TableColumnsInterface => ({
  value: "amount",
  title,
  format: TableDataType.STRING,
  sorting,
  customRender: (value: unknown, row?: any) => {
    const coin = row?.coin || "USD"
    const amount = (value as number | string | null) ?? row?.usd_amount
    if (isFiatCoin(coin)) return formatFiatCurrency((amount as number | undefined) ?? undefined, "-", coin)
    if (!amount) return "-"
    return formatCryptoCurrency(amount as number | string)
  }
})

const usdMoneyColumn = (value: string, title: string): TableColumnsInterface => ({
  value,
  title,
  format: TableDataType.FIAT_CURRENCY,
  sorting: true
})

// Total spendable balance (cash + bonus) captured pre-debit at bet time.
// Not a NUX sort key, so the native variant is not sortable.
export const userBalanceNativeColumn = nativeMoneyColumn("user_balance_before", "User Balance", false)
export const userBalanceUsdColumn = usdMoneyColumn("user_fiat_balance", "User Balance USD")

// Stake / winnings. Same key on both sources — only the unit differs.
export const fiatAmountNativeColumn = nativeMoneyColumn("fiat_amount", "Amount")
export const fiatAmountUsdColumn = usdMoneyColumn("fiat_amount", "Fiat Amount")
export const winFiatAmountNativeColumn = nativeMoneyColumn("win_fiat_amount", "Amount Won")
export const winFiatAmountUsdColumn = usdMoneyColumn("win_fiat_amount", "Fiat Amount Won")

/**
 * Derive the BetBy (MNT) column set from a casino (NUX) one by swapping every native money column
 * back to its USD counterpart. Keyed by the native column's `value` — note the balance columns use
 * DIFFERENT keys per source (`user_balance_before` vs `user_fiat_balance`), which is deliberate:
 * the NUX field is native and must never be served under the USD name.
 */
const betbyColumnOverrides: Record<string, TableColumnsInterface> = {
  [userBalanceNativeColumn.value]: userBalanceUsdColumn,
  [fiatAmountNativeColumn.value]: fiatAmountUsdColumn,
  [winFiatAmountNativeColumn.value]: winFiatAmountUsdColumn
}

export const toBetbyColumns = (columns: TableColumnsInterface[]): TableColumnsInterface[] =>
  columns.map((column) => betbyColumnOverrides[column.value] ?? column)

// The only keys the casino bets endpoints sort by; any other key silently falls back to created_at.
const CASINO_BET_SORT_KEYS = new Set(["fiat_amount", "crypto_amount", "win_fiat_amount", "win_crypto_amount", "created_at"])

const toCasinoColumn = (column: TableColumnsInterface): TableColumnsInterface => {
  if (CASINO_BET_SORT_KEYS.has(column.value)) {
    return column
  }
  return { ...column, sorting: false }
}

export const toCasinoColumns = (columns: TableColumnsInterface[]): TableColumnsInterface[] => columns.map(toCasinoColumn)

// `sorting` reflects the BetBy path; toCasinoColumns narrows it for the casino tab.
const userBetsColumns: TableColumnsInterface[] = [
  {
    value: "id",
    title: "Id",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "gameSessionId",
    title: "Session ID",
    format: TableDataType.STRING,
    sorting: false,
    mntSorting: true
  },
  {
    value: "provider_name",
    title: "Provider",
    format: TableDataType.STRING,
    sorting: false,
    mntSorting: true
  },
  {
    value: "type",
    title: "Bet Type",
    format: TableDataType.STRING,
    sorting: false,
    mntSorting: true
  },
  {
    value: "sportsbet_status",
    title: "Sportsbet status",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "game_name",
    title: "Game Name",
    format: TableDataType.STRING,
    sorting: true
  },

  fiatAmountNativeColumn,
  {
    value: "crypto_amount",
    title: "Crypto Amount",
    format: TableDataType.CRYPTO_CURRENCY,
    sorting: true
  },
  winFiatAmountNativeColumn,
  {
    value: "win_crypto_amount",
    title: "Crypto Amount Won",
    format: TableDataType.CRYPTO_CURRENCY,
    sorting: true
  },
  {
    value: "coin",
    title: "Coin",
    format: TableDataType.STRING,
    sorting: true
  },
  userBalanceNativeColumn,
  {
    value: "finished",
    title: "Finished",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "rollbacked",
    title: "Rollbacked",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "created_at",
    title: "Created At",
    format: TableDataType.DATE,
    sorting: true
  }
]

export const tableUserBets: TableColumnsInterface[] = toCasinoColumns(userBetsColumns)

/**
 * BetBy bets tab — identical to the casino columns except every money column reverts to USD.
 * BetBy is monolith-owned (migrates last), so its rows are USD cents, not native.
 */
export const tableUserBetbyBets: TableColumnsInterface[] = toBetbyColumns(userBetsColumns)

/**
 * Casino "Bets" tab column set for the current tenant. Native (NUX) amounts only exist for
 * microservice tenants; monolith-only tenants never emit them, so they keep the pre-native USD
 * columns — which are exactly the reverted-to-USD BetBy set.
 */
export const getUserBetsColumns = (): TableColumnsInterface[] => (isMicroserviceTenant() ? tableUserBets : tableUserBetbyBets)

// A getter, not a module-level const: `isMicroserviceTenant()` is resolved from the backend after
// login, so the column set must be computed at render time.
export const getUserTransactionsColumns = (): TableColumnsInterface[] => [
  {
    value: "id",
    title: "Id",
    format: TableDataType.STRING,
    sorting: true
  },
  // {
  //   value: "tx_id",
  //   title: "Tx Id",
  //   format: TableDataType.STRING,
  //   sorting: false
  // },
  {
    value: "coin",
    title: "Coin",
    format: TableDataType.STRING,
    sorting: true
  },
  // The column was titled "Amount" but formatted as CRYPTO_CURRENCY, so on a microservice tenant
  // — where `amount` is minor units of the row's own fiat coin — it divided cents by 1e8 and
  // showed a $10.00 withdrawal as "0.00001".
  isMicroserviceTenant()
    ? transactionAmountNativeColumn("Amount", false)
    : {
        value: "amount",
        title: "Amount",
        format: TableDataType.CRYPTO_CURRENCY,
        sorting: true
      },
  {
    value: "usd_amount",
    title: "USD Amount",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "cardNumber",
    title: "Card Number",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "status",
    title: "Status",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "type",
    title: "Type",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    // Only populated for withdrawals; backend reads it from `other.reason`
    value: "declineReason",
    title: "Decline Reason",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "created_at",
    title: "Created At",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "updated_at",
    title: "Updated At",
    format: TableDataType.DATE,
    sorting: true
  }
]

export const tableUserReferrals: TableColumnsInterface[] = [
  {
    value: "user_id",
    title: "User Id",
    format: TableDataType.NUMBER,
    sorting: false
  },
  {
    value: "activated",
    title: "Activated",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "id",
    title: "Id",
    format: TableDataType.NUMBER,
    sorting: false
  },
  {
    value: "display_name",
    title: "Display Name",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "username",
    title: "Username",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "email_address",
    title: "Email Address",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "email_verified",
    title: "Email Verified",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "role",
    title: "Role",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "rakeback_earned",
    title: "Rakeback Earned",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "rakeback_balance",
    title: "Rakeback Balance",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "created_at",
    title: "Created At",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "phone_number",
    title: "Phone Number",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "phone_number_verified",
    title: "Phone Number Verified",
    format: TableDataType.BOOLEAN,
    sorting: true
  }
]

export const trustedStatuses = [
  {
    value: "TRUSTED",
    name: "TRUSTED"
  },
  {
    value: "UNTRUSTED",
    name: "UNTRUSTED"
  },
  {
    value: "INVALID",
    name: "INVALID"
  },
  {
    value: "NOT_STARTED",
    name: "NOT STARTED"
  }
]

export const roles = [
  {
    value: "ADMIN",
    name: "ADMIN"
  },
  {
    value: "OPERATOR",
    name: "OPERATOR"
  },
  {
    value: "SUPPORT",
    name: "SUPPORT"
  },
  {
    value: "VIP_SUPPORT",
    name: "VIP_SUPPORT"
  },
  {
    value: "USER",
    name: "USER"
  },
  {
    value: "REFMAN",
    name: "REFMAN"
  }
]

export const Genders = [
  {
    value: "Male",
    name: "Male"
  },
  {
    value: "Female",
    name: "Female"
  }
]

export const coins = [
  {
    value: "BTC",
    name: "BTC"
  },
  {
    value: "ETH",
    name: "ETH"
  },
  {
    value: "LTC",
    name: "LTC"
  }
]

export const transactionStatuses = [
  {
    value: "PENDING",
    name: "PENDING"
  },
  {
    value: "COMPLETED",
    name: "COMPLETED"
  },
  {
    value: "CONFIRMED",
    name: "CONFIRMED"
  },
  {
    value: "UNCONFIRMED",
    name: "UNCONFIRMED"
  },
  {
    value: "DECLINED",
    name: "DECLINED"
  },
  {
    value: "FAILED",
    name: "FAILED"
  },
  {
    value: "REFUNDED",
    name: "REFUNDED"
  }
]

export const transactionTypes = [
  {
    value: "DEPOSIT",
    name: "DEPOSIT"
  },
  {
    value: "WITHDRAWAL",
    name: "WITHDRAWAL"
  },
  {
    value: "TRANSFER",
    name: "TRANSFER"
  },
  {
    value: "REFUND",
    name: "REFUND"
  },
  {
    value: "BET_ROLLBACK",
    name: "BET ROLLBACK"
  },
  {
    value: "WIN_ROLLBACK",
    name: "WIN ROLLBACK"
  },
  {
    value: "RAKEBACK",
    name: "RAKEBACK"
  },
  {
    value: "RAKEBACK_ROLLBACK",
    name: "RAKEBACK ROLLBACK"
  },
  {
    value: "RAKEBACK_CLAIM",
    name: "RAKEBACK CLAIM"
  },
  {
    value: "AFFILIATE_PAY",
    name: "AFFILIATE PAY"
  },
  {
    value: "AFFILIATE_ROLLBACK",
    name: "AFFILIATE ROLLBACK"
  },
  {
    value: "AFFILIATE_CLAIM",
    name: "AFFILIATE CLAIM"
  },
  {
    value: "RAKE",
    name: "RAKE"
  },
  {
    value: "BET",
    name: "BET"
  },
  {
    value: "WIN",
    name: "WIN"
  },
  {
    value: "BONUS_CLAIM",
    name: "BONUS CLAIM"
  },
  {
    value: "BONUS_EXPIRE",
    name: "BONUS EXPIRE"
  },
  {
    value: "BONUS_CANCEL",
    name: "BONUS CANCEL"
  },
  {
    value: "BONUS_TRANSFER",
    name: "BONUS TRANSFER"
  },
  {
    value: "CLAIM_DAILY_REWARD",
    name: "DAILY REWARD"
  },
  {
    value: "CLAIM_WEEKLY_REWARD",
    name: "WEEKLY REWARD"
  },
  {
    value: "CLAIM_MONTHLY_REWARD",
    name: "MONTHLY REWARD"
  },
  {
    value: "VIP_REWARD",
    name: "VIP REWARD"
  },
  {
    value: "TIP",
    name: "TIP"
  },
  {
    value: "CHALLENGE_PRIZE",
    name: "CHALLENGE REWARD"
  },
  {
    value: "OTHER",
    name: "OTHER"
  }
]

export const tableUserRewardGames: TableColumnsInterface[] = [
  {
    value: "id",
    title: "Id",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "user_id",
    title: "User Id",
    format: TableDataType.NUMBER,
    sorting: false,
    mntSorting: true
  },
  {
    value: "title",
    title: "Game Name",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "game_identifier",
    title: "Game Identifier",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "issue_id",
    title: "Issue Id",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "status",
    title: "Status",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "used",
    title: "Used",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "win_fiat_amount",
    title: "win_fiat_amount",
    format: TableDataType.NUMBER,
    sorting: true
  }
]

export const formatTransactionTypes = () => {
  return transactionTypes.reduce((obj, item) => Object.assign(obj, { [item.value]: item.name }), {})
}

export const tableBonusesColumns: TableColumnsInterface[] = [
  {
    value: "id",
    title: "Id",
    format: TableDataType.SHORTENED_STRING,
    sorting: false
  },
  {
    value: "title",
    title: "Title",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "eligibility",
    title: "Eligibility",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "banner.banner_image",
    title: "Banner",
    format: TableDataType.IMAGE,
    sorting: false
  },
  {
    value: "tenant_id",
    title: "Tenant",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "active",
    title: "Active",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "bonus_percent",
    title: "Bonus Percent",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "min_deposit",
    title: "Min Deposit Amount",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "max_bonus_amount",
    title: "Max Bonus Amount",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "max_usage_per_user",
    title: "Max Usage Per User",
    format: TableDataType.NUMBER,
    sorting: false
  },
  {
    value: "days_to_expire",
    title: "Days to Expire",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "wager_requirements",
    title: "Wager Reqruirements",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "schedule",
    title: "Schedule",
    format: TableDataType.DATE_PICKER,
    sorting: false,
    mntSorting: true
  }
]

// Freespins bonuses
export const tableFSBonusesColumns: TableColumnsInterface[] = [
  ...tableBonusesColumns,
  {
    value: "gameIdentifier",
    title: "Game Identifier",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "freespinsAmount",
    title: "Freespins Amount",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "pricePerSpin",
    title: "Price Per Spin",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "event",
    title: "Event",
    format: TableDataType.STRING,
    sorting: true
  }
]

export const tableComments: TableColumnsInterface[] = [
  {
    value: "commentor",
    title: "Commentor",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "created_at",
    title: "Created",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "comment",
    title: "Comment",
    format: TableDataType.STRING,
    sorting: true
  }
]

export const tableReferrers: TableColumnsInterface[] = [
  {
    value: "referrer_username",
    title: "Username",
    format: TableDataType.STRING,
    sorting: true,
    mntSorting: false
  },
  {
    value: "referrals_links_number",
    title: "Referrals",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "referrals_deposits",
    title: "Referral Deposits",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "referrals_wagers",
    title: "Referrals Wagers",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  }
]

export const tableReferralsCodes: TableColumnsInterface[] = [
  {
    value: "code",
    title: "Code",
    format: TableDataType.STRING,
    sorting: true,
    mntSorting: false
  },
  {
    value: "referrals_number",
    title: "Referrals",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "referrals_deposits",
    title: "Total Deposits",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "referral_earned",
    title: "Total Earned",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "referral_wager",
    title: "Total Wager",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  }
]

export const tableReferrals: TableColumnsInterface[] = [
  {
    value: "username",
    title: "Username",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "referrals_deposits",
    title: "Total Deposits",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "referral_wager",
    title: "Total Wager",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "created_at",
    title: "Created",
    format: TableDataType.DATE,
    sorting: true
  }
]

export const tableBalanceProcessors: TableColumnsInterface[] = [
  { value: "processor", title: "Processor", format: TableDataType.STRING, sorting: true },
  {
    value: "volume_usd_cents",
    title: "Volume (USD)",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    customRender: (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))
  },
  { value: "tx_count", title: "Tx Count", format: TableDataType.NUMBER, sorting: true },
  {
    value: "rolling_reserve_usd_cents",
    title: "Rolling Reserve (USD)",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    customRender: (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))
  },
  {
    value: "fee_total_usd_cents",
    title: "Fees (USD)",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    customRender: (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))
  },
  {
    value: "available_usd_cents",
    title: "Available (USD)",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    customRender: (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))
  }
]

export const tableBalanceMethods: TableColumnsInterface[] = [
  { value: "payment_method", title: "Payment method", format: TableDataType.STRING, sorting: true },
  {
    value: "volume_usd_cents",
    title: "Volume (USD)",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    customRender: (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))
  },
  { value: "tx_count", title: "Tx Count", format: TableDataType.NUMBER, sorting: true },
  {
    value: "rolling_reserve_usd_cents",
    title: "Rolling Reserve (USD)",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    customRender: (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))
  },
  {
    value: "fee_total_usd_cents",
    title: "Fees (USD)",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    customRender: (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))
  },
  {
    value: "available_usd_cents",
    title: "Available (USD)",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    customRender: (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))
  }
]

export const tableBalanceMethodsWithdrawal: TableColumnsInterface[] = [
  ...tableBalanceMethods,
  {
    value: "psp_balance_cents",
    title: "PSP Balance (USD)",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true,
    customRender: (v: number) => (v === 0 ? "–" : formatFiatCurrency(v))
  }
]

export const tablePromotionsColumns: TableColumnsInterface[] = [
  {
    value: "created_at",
    title: "Created At",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "slug",
    title: "Slug",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "title",
    title: "Title",
    format: TableDataType.STRING,
    sorting: false,
    mntSorting: true
  },
  {
    value: "short_title",
    title: "Short Title",
    format: TableDataType.STRING,
    sorting: false,
    mntSorting: true
  },
  {
    value: "categories",
    title: "Categories",
    format: TableDataType.STRING,
    sorting: false,
    customRender: (raw: PromotionCategory[] | null | undefined) => {
      if (!raw || !raw.length) return "-"

      const labelMap: Record<PromotionCategory, string> = {
        CASINO: "Casino",
        SPORTS: "Sports",
        VIP: "VIP",
        FREEPLAY: "Free Cash",
        NEWS: "News"
      }

      return raw.map((cat) => labelMap[cat] ?? cat).join(", ")
    }
  },
  {
    value: "status",
    title: "Status",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "is_active",
    title: "Active",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "is_featured",
    title: "Featured",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "start_at",
    title: "Start At",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "end_at",
    title: "End At",
    format: TableDataType.DATE,
    sorting: true
  }
]

// ============================================================================
// Tournament list – table columns
// ============================================================================

export const tableTournamentColumns = [
  {
    value: "name",
    title: "Name",
    sorting: true,
    format: (row: Tournament) => (
      <div>
        <div className="font-medium">{row.name}</div>
        {row.description && <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{row.description}</div>}
      </div>
    )
  },
  {
    value: "status",
    title: "Status",
    sorting: true,
    format: (row: Tournament) => getStatusBadge(row.status)
  },
  {
    value: "scoring_mode",
    title: "Mode",
    sorting: true,
    format: (row: Tournament) => <span className="capitalize">{row.scoring_mode.toLowerCase().replace("_", " ")}</span>
  },
  {
    value: "start_time",
    title: "Start Time",
    sorting: true,
    format: (row: Tournament) => new Date(row.start_time).toLocaleString()
  },
  {
    value: "end_time",
    title: "End Time",
    sorting: true,
    format: (row: Tournament) => new Date(row.end_time).toLocaleString()
  },
  {
    value: "prize_pool_base",
    title: "Prize Pool",
    sorting: true,
    format: (row: Tournament) => {
      if (!row.prize_pool_base) return "-"
      return `$${Number(row.prize_pool_base).toFixed(2)}`
    }
  },
  {
    value: "max_participants",
    title: "Max Players",
    sorting: true,
    format: (row: Tournament) => row.max_participants || "Unlimited"
  }
]
